// Orchestrator Review Queue — production page.
//
// Role-aware: same component renders /buyer/worklist (role='buyer') and
// /engineering/review-queue (role='engineer'). Differences (KPI labels,
// action gating, tooltips) come from a RoleConfig lookup so behavior
// changes without code branches outside the config.
//
// Replaces:
//   • BuyerWorklistMockPage (mock — kept for reference until removal)
//   • BuyerTriageQueuePage (deprecated per PRD §D20 / Implementation Plan §7.2)
//   • EngineeringReviewQueuePage (deprecated per PRD §D20)
//
// Data comes from /api/orchestrator/work-items and friends via React Query.

import React, { useCallback, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Button, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, IconButton, alpha, useTheme,
  Tooltip, Menu, MenuItem, ListItemIcon,
  ListItemText, Divider, TablePagination, CircularProgress, Alert, Skeleton,
  Collapse, Chip, Select, FormControl, InputLabel, OutlinedInput, Checkbox,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import {
  Refresh as RefreshIcon, OpenInNew as OpenIcon,
  Snooze as SnoozeIcon, PersonAdd as AssignIcon,
  MoreVert as MoreIcon, AccessTime as ClockIcon,
  CheckCircle as CheckIcon, Block as DismissIcon,
  ExpandMore as ExpandMoreIcon, ChevronRight as ChevronRightIcon,
  RateReviewOutlined as DrawingIcon,
  FilterList as FilterIcon,
  SwapHoriz as SwapViewIcon,
  AccessTimeFilled as UrgentIcon,
  LightbulbOutlined as SavingsIcon,
  InboxOutlined as TriageIcon,
  Inventory2Outlined as PartsIcon,
  CompareArrowsOutlined as AlternativesIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import bomV2Service from '../../services/bomV2Service';
import UnifiedSearchBar, { UnifiedSearchValue } from '../../components/search/UnifiedSearchBar';

import {
  TriggerBadge, MineGauge, TriggerFilterStrip, SubChip, PartIdentity, FffScoreGauge, WiTypeIcon,
  WI_TYPE_META, type WiCategory,
  AlternativesTable, RfqWorkItemCard,
  AltActionConfirmDialog,
  SEVERITY_COLOR, formatPrice, formatRevenue, ltColor, pctDiff,
  TriggerType, Severity,
  TRIGGER_CONFIG,
} from '../../components/orchestrator';
import type { AltActionKind } from '../../components/orchestrator';
import GaugePicker from '../../components/orchestrator/GaugePicker';
import ChangeApprovalCard, { dueIn } from '../../components/orchestrator/ChangeApprovalCard';
import SupplierChangeRequestCard from '../../components/orchestrator/SupplierChangeRequestCard';
import PackageGapsCard from '../../components/orchestrator/PackageGapsCard';
import {
  GAUGE_REGISTRY,
  ROLE_DEFAULT_GAUGES,
  loadUserGaugeSelection,
  type GaugeDef,
} from '../../components/orchestrator/gauges-registry';
import {
  useOrchestratorWorklist, useOrchestratorCounts, useOrchestratorKpis,
  useExecuteAction, useWorkItemDetail,
} from '../../hooks/useOrchestrator';
import { WorkItemRole } from '../../services/orchestratorService';
import { ChangeSeverityMark } from '../../components/changes/ChangeSeverityMark';

// ── Role config (PRD §18 D21) ────────────────────────────────

interface RoleConfig {
  title: string;
  kpis: { altsToReviewLabel: string; approvedByYouLabel: string };
}

const ROLE_CONFIG: Record<WorkItemRole, RoleConfig> = {
  buyer: {
    title: 'Buyer Review Queue',
    kpis: { altsToReviewLabel: 'Alternative to Review', approvedByYouLabel: 'Approved by you' },
  },
  engineer: {
    title: 'Engineer Review Queue',
    kpis: { altsToReviewLabel: 'Alternatives awaiting FFF', approvedByYouLabel: 'FFF approved by you' },
  },
};

// ── BOM-driven priority (PRD-BOM-Priority-System) ────────────
// Color is the only signal — P3 (routine) stays neutral so it recedes.
const PRIORITY_COLOR: Record<string, string> = {
  P1: '#d2453f',
  P2: '#c98a2e',
  P3: '#94a3b8',
};

// Soft halo ring behind the dot (P1/P2 only — P3 recedes, no ring).
const PRIORITY_HALO: Record<string, string> = {
  P1: 'rgba(210, 69, 63, 0.15)',
  P2: 'rgba(201, 138, 46, 0.15)',
  P3: 'transparent',
};

// Short driver keyword per primary reason code — the at-a-glance "why".
// The full reasoning (priority.summary / reasons[]) is shown on click (Phase 2).
const PRIORITY_DRIVER_LABEL: Record<string, string> = {
  CRITICAL_PART: 'critical part',
  CRITICAL_MATERIAL: 'critical material',
  SINGLE_SOURCE: 'single-source',
  LONG_LEAD: 'long lead',
  WEAK_FFF: 'weak match',
  LIMITED_SOURCE: 'limited supply',
  MODERATE_LEAD: 'lead time',
  CUSTOM_MADE: 'custom-made',
  HIGH_EXPOSURE: '$ at risk',
  ALT_APPROVED: 'alt approved',
  ROUTINE_DEFAULT: 'routine',
  DUE_NOW: 'due now',
  DUE_SOON: 'due soon',
  AGING: 'aging',
  STOCKOUT_SOON: 'low stock',
  VERY_HIGH_EXPOSURE: 'high $ risk',
};

// Drawing-collab triggers don't have alternatives — they're conversation
// threads pinned to a drawing rev. The queue row renders type-aware affordances
// for these (no "alts" expansion, deep-link → drawing workspace instead).
const DRAWING_COLLAB_TRIGGERS: ReadonlySet<string> = new Set([
  'DRAWING_CLARIFICATION',
  'DRAWING_DEVIATION',
  'DRAWING_REV_CHANGED',
  'DRAWING_BROADCAST',
  'DRAWING_MFG_NOTE',
]);

const isDrawingCollabTrigger = (t: unknown): boolean =>
  typeof t === 'string' && DRAWING_COLLAB_TRIGGERS.has(t);

// ── O.1 — Type / Subtype axes (unified Orchestrator filtering) ──────
//
// The 3-axis filter model: Role (already wired) × Type (category) × Subtype
// (taskType). Backend `categories` enum lives in InboxQueryDto; `taskTypes`
// is a free string but each Type bucket pins down a small known set.
//
// `CATEGORY_OPTIONS` drives the Type chip strip; the matching Subtype map
// gates which taskType options the Subtype <Select> exposes once a Type
// (or set of Types) is picked.

type CategoryId = 'RFQ' | 'SUPPLIER_LINK' | 'DRAWING_QA' | 'CROSSREF_REVIEW' | 'PCN' | 'CATALOG_REQUEST' | 'CHANGE' | 'PACKAGE' | 'OTHER';

interface CategoryOption {
  id: CategoryId;
  label: string;
}

const CATEGORY_OPTIONS: CategoryOption[] = [
  { id: 'RFQ',              label: 'RFQ' },
  { id: 'SUPPLIER_LINK',    label: 'Supplier Link' },
  { id: 'DRAWING_QA',       label: 'Drawing Q&A' },
  { id: 'CROSSREF_REVIEW',  label: 'Cross-ref' },
  { id: 'PCN',              label: 'PCN' },
  { id: 'CATALOG_REQUEST',  label: 'ERP Catalog Request' },
  { id: 'CHANGE',           label: 'Changes' },
  { id: 'PACKAGE',          label: 'Package gaps' },
  { id: 'OTHER',            label: 'Other' },
];

// Map each Type → its valid Subtypes (taskType enum values) + human labels.
// Source: O.1 spec — RFQ subtypes, supplier-link approval, drawing-collab
// task types, cross-ref new-alternative review, PCN/EOL/obsolescence
// response prompts.
const SUBTYPES_BY_CATEGORY: Record<CategoryId, Array<{ value: string; label: string }>> = {
  RFQ: [
    { value: 'RFQ_DRAFT_REVIEW',       label: 'Draft to review' },
    { value: 'RFQ_RESPONSE_RECEIVED',  label: 'Quote received' },
    { value: 'RFQ_FOLLOW_UP_DUE',      label: 'Follow-up due' },
    { value: 'RFQ_TIMEOUT',            label: 'Timed out' },
  ],
  SUPPLIER_LINK: [
    { value: 'SUPPLIER_LINK_APPROVAL', label: 'Supplier link approval' },
  ],
  DRAWING_QA: [
    { value: 'SUPPLIER_DRAWING_QUESTION',     label: 'Supplier question' },
    { value: 'SUPPLIER_DEVIATION_REQUEST',    label: 'Deviation request' },
    { value: 'SUPPLIER_CHANGE_REQUEST',       label: 'Change request' },
    { value: 'SUPPLIER_MFG_NOTE',             label: 'Manufacturing note' },
    { value: 'ENGINEER_REVISION_RE_CONFIRM',  label: 'Re-confirm revision' },
    { value: 'SUPPLIER_ACCEPTANCE_PENDING',   label: 'Acceptance pending' },
  ],
  CROSSREF_REVIEW: [
    { value: 'NEW_ALTERNATIVE_REVIEW',  label: 'New alternative to review' },
  ],
  PCN: [
    { value: 'PCN_RESPONSE',           label: 'PCN response' },
    { value: 'EOL_RESPONSE',           label: 'EOL response' },
    { value: 'OBSOLESCENCE_RESPONSE',  label: 'Obsolescence response' },
  ],
  CATALOG_REQUEST: [
    { value: 'ERP_CATALOG_REQUEST',    label: 'Register as ERP item' },
  ],
  PACKAGE: [
    { value: 'PACKAGE_GAPS_REVIEW',    label: 'Review package gaps' },
  ],
  CHANGE: [
    { value: 'CHANGE_APPROVAL',        label: 'Approval step' },
  ],
  OTHER: [],
};

// Lazy detail loader for an expanded worklist row. Fetches the rich detail
// envelope (alternatives) only when the row is open, so collapsed rows don't
// pay the join cost.
interface WorklistRowDetailProps {
  workItemId: string;
  /** Master part `_id` — used to build the FFF compare route. */
  masterPartId?: string;
  role: WorkItemRole;
  busyActionId: string | null;
  /** Master part's current best supplier price — used for trend% in Alternatives. */
  currentBestPrice?: number;
  onAltAction: (
    actionId: 'APPROVE_ALT_AS_BUYER' | 'REJECT_ALT_AS_BUYER'
            | 'APPROVE_ALT_AS_ENGINEER' | 'REJECT_ALT_AS_ENGINEER'
            | 'APPROVE_ALT_CONDITIONAL',
    altRow: any,
  ) => void;
  /** Navigate to the cross-ref compare page (with work-item context) for a given alt. */
  onOpenCompare: (altPartId: string, partCrossReferenceId: string) => void;
}

const WorklistRowDetail: React.FC<WorklistRowDetailProps> = ({
  workItemId, role, busyActionId, currentBestPrice, onAltAction, onOpenCompare,
}) => {
  const { data, isLoading, isError, error } = useWorkItemDetail(workItemId);
  if (isLoading) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress size={20} />
      </Box>
    );
  }
  if (isError || !data) {
    return (
      <Alert severity="error" sx={{ my: 1 }}>
        Failed to load details. {(error as any)?.message || ''}
      </Alert>
    );
  }
  const refs = (data as any).refs ?? {};
  return (
    <Box>
      <AlternativesTable
        rows={refs.alternatives ?? []}
        role={role}
        availableActions={(data as any).availableActions ?? []}
        busyActionId={busyActionId ?? undefined}
        currentBestPrice={currentBestPrice}
        onAction={onAltAction}
        onOpenCompare={(row) => row.altPartId && row.partCrossReferenceId && onOpenCompare(row.altPartId, row.partCrossReferenceId)}
      />
    </Box>
  );
};

interface OrchestratorReviewQueuePageProps {
  role?: WorkItemRole;
}

const OrchestratorReviewQueuePage: React.FC<OrchestratorReviewQueuePageProps> = ({ role = 'buyer' }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const cfg = ROLE_CONFIG[role];

  // ── Filter / pagination state ─────────────────────────────

  const [activeTriggers, setActiveTriggers] = useState<Set<TriggerType>>(new Set());
  // O.1 — Type axis (categories). Multi-select; empty = no Type filter.
  const [activeCategories, setActiveCategories] = useState<Set<CategoryId>>(new Set());
  // Lens gauges keyed by what an item asks of you (Approvals); a plain on/off.
  const [activeLenses, setActiveLenses] = useState<Set<string>>(new Set());
  // Per-category sub-filter (taskType) selections — keyed by category. Empty
  // / missing set means "all subtypes for this category." Drives the per-
  // category sub-chip row and the filterGroups payload sent to the backend.
  const [categorySubtypes, setCategorySubtypes] = useState<Map<CategoryId, Set<string>>>(new Map());
  // Collapse state for the sub-filter tray. Expanded on add / customize;
  // auto-collapses when the user clicks anywhere outside the tray (e.g. the
  // table, search bar). Active gauges stay visually highlighted in the
  // strip so the user always sees what's filtering — they can click a
  // gauge again to re-expand the tray for customization.
  const [subFilterExpanded, setSubFilterExpanded] = useState(false);
  // Which active group's sub-options are currently visible in the tray.
  // Most-recently-clicked active group wins. `null` ⇒ no row shown.
  // Click semantics on a gauge (decided with user, doc'd in click handler):
  //   - inactive  → enable + focus + expand
  //   - active & focused → DISABLE + clear focus (toggle off)
  //   - active & unfocused → FOCUS only (switch view, keep enabled)
  // This lets the user see one filter group's sub-row at a time without
  // cluttering the strip with every active group's options.
  const [focusedGroupKey, setFocusedGroupKey] = useState<string | null>(null);
  const subFilterRef = React.useRef<HTMLDivElement | null>(null);
  // Outside-click listener — collapses the tray when the user clicks
  // anywhere that isn't the tray or a gauge (gauges keep their own
  // expand-on-click behavior wired in the click handler).
  React.useEffect(() => {
    if (!subFilterExpanded) return;
    const handler = (e: MouseEvent) => {
      const tray = subFilterRef.current;
      if (!tray) return;
      const target = e.target as Node | null;
      if (target && tray.contains(target)) return;
      // Skip when the click landed on a gauge — gauges call setSubFilterExpanded
      // explicitly. Detection: look for the closest data-gauge attribute.
      let node = target as (HTMLElement | null);
      while (node) {
        if (node.dataset && node.dataset.gauge === '1') return;
        node = node.parentElement;
      }
      setSubFilterExpanded(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [subFilterExpanded]);
  // O.1 — Subtype axis (taskTypes). Multi-select; only relevant when at least
  // one Type is selected. Pruned automatically when a Type is unselected.
  const [activeSubtypes, setActiveSubtypes] = useState<Set<string>>(new Set());
  // Filter strip is collapsed by default — opens via the filter-toggle icon
  // under the search bar. When any trigger is active we auto-show so the user
  // can see (and clear) what's currently filtering.
  const [filtersOpen, setFiltersOpen] = useState(false);
  // Gauge selection — every user fully controls which gauges appear in their
  // strip. `null` means "use role defaults" (the initial state until the user
  // touches the (+) picker). After a customization, the picker writes the
  // role-scoped localStorage key with the explicit set.
  const [userGaugeSelection, setUserGaugeSelection] = useState<string[] | null>(
    () => loadUserGaugeSelection(role),
  );
  React.useEffect(() => {
    setUserGaugeSelection(loadUserGaugeSelection(role));
  }, [role]);
  const effectiveGaugeIds: string[] = userGaugeSelection && userGaugeSelection.length > 0
    ? userGaugeSelection
    : (ROLE_DEFAULT_GAUGES[role] ?? ROLE_DEFAULT_GAUGES.buyer);
  // Unified search bar — PN/MPN + free-text segments. Both feed the single
  // worklist `q` param (client/back-end free-text over part/MPN/alt).
  const [identifier, setIdentifier] = useState('');
  const [text, setText] = useState('');
  const [onlyMine, setOnlyMine] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  // `step`: a step of an approval flow, which closes by being decided, never by hand.
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; itemId: string; step?: boolean } | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [altActionBusy, setAltActionBusy] = useState<string | null>(null);

  // Pending alt action — opens the confirm dialog. The whole action+row is
  // staged here so on confirm we know exactly which xref to dispatch against.
  const [pendingAltAction, setPendingAltAction] = useState<
    | { workItemId: string; actionId: string; altRow: any; kind: AltActionKind }
    | null
  >(null);

  // BOM filter state ─ stores the picked BOM so the segment can render its name
  // without re-fetching; bomId is what gets sent to the worklist + counts APIs.
  // The picker UI now lives inside the UnifiedSearchBar's BOM segment.
  const [bomFilter, setBomFilter] = useState<{ bomId: string; bomName: string } | null>(null);

  // Async source for the bar's persistent BOM dropdown.
  const fetchBoms = useCallback(async (q: string) => {
    const res = await bomV2Service.listBoms({ query: q || undefined, limit: 20 });
    const items = (res?.items ?? []) as any[];
    return items.map((b) => ({ bomId: b.bomId ?? b._id, bomName: b.bomName ?? b.name ?? b.bomId }));
  }, []);

  // Bar value + change handler — diffs each segment, resets to page 0 on change.
  const searchValue: UnifiedSearchValue = {
    identifier,
    text,
    bomId: bomFilter?.bomId ?? null,
    bomName: bomFilter?.bomName,
  };
  const handleSearchChange = useCallback(
    (next: UnifiedSearchValue) => {
      let changed = false;
      if (next.identifier !== identifier) { setIdentifier(next.identifier); changed = true; }
      if (next.text !== text) { setText(next.text); changed = true; }
      const curBom = bomFilter?.bomId ?? null;
      if (next.bomId !== curBom) {
        setBomFilter(next.bomId ? { bomId: next.bomId, bomName: next.bomName || next.bomId } : null);
        changed = true;
      }
      if (changed) setPage(0);
    },
    [identifier, text, bomFilter],
  );

  const toggleExpand = (workItemId: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(workItemId)) next.delete(workItemId);
      else next.add(workItemId);
      return next;
    });
  };

  // Deep-link a drawing-collab WorkItem row to its canvas at the specific
  // thread. Falls back to plain part/drawing route if the thread anchor is
  // missing (defensive — taskPayload should always include threadId for these).
  const openDrawingThread = useCallback(
    (item: any) => {
      const tp = item?.taskPayload ?? {};
      const partId: string | undefined = tp.partId ?? item.subjectId;
      const threadId: string | undefined = tp.threadId;
      if (!partId) return;
      const qs = threadId ? `?threadId=${encodeURIComponent(threadId)}` : '';
      navigate(`/parts/${encodeURIComponent(partId)}/drawing${qs}`);
    },
    [navigate],
  );

  // Phase 1 simplification: no current-user-id wired yet → "Mine" passes a
  // sentinel value that the backend treats as "current authenticated user".
  // When auth-context is available globally, replace with the real id.
  const myUserId = (typeof window !== 'undefined' && (window as any).__currentUserId) || undefined;

  // O.1 — Subtype options narrow to whichever Types are currently selected.
  // When no Type is selected we expose every known Subtype across all Types so
  // the user can still pre-filter by Subtype alone (backend treats taskTypes
  // independently of categories).
  const availableSubtypes = useMemo(() => {
    const cats = activeCategories.size > 0
      ? Array.from(activeCategories)
      : (Object.keys(SUBTYPES_BY_CATEGORY) as CategoryId[]);
    const out: Array<{ value: string; label: string }> = [];
    const seen = new Set<string>();
    for (const c of cats) {
      for (const opt of SUBTYPES_BY_CATEGORY[c]) {
        if (!seen.has(opt.value)) {
          seen.add(opt.value);
          out.push(opt);
        }
      }
    }
    return out;
  }, [activeCategories]);

  const handleSubtypeChange = useCallback((event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    const next = new Set<string>(typeof value === 'string' ? value.split(',') : value);
    setActiveSubtypes(next);
    setPage(0);
  }, []);

  // Multi-group OR filter. When the user has any category or trigger gauge
  // active we send a filterGroups payload so the backend OR's the groups
  // (per-category subtype pairing preserved). Falls back to the flat
  // categories / triggerTypes / taskTypes axes when nothing's selected
  // (legacy callers and back-compat for tests).
  const filterGroups: Array<{ category?: string; taskTypes?: string[]; triggerTypes?: string[] }> = [];
  for (const cat of activeCategories) {
    const subs = categorySubtypes.get(cat);
    filterGroups.push({
      category: cat,
      ...(subs && subs.size > 0 ? { taskTypes: Array.from(subs) } : {}),
    });
  }
  if (activeTriggers.size > 0) {
    // All active triggers (across all selected trigger gauges) combine into
    // a single `$in` clause — one IXSCAN on `triggerType` instead of N.
    filterGroups.push({ triggerTypes: Array.from(activeTriggers) });
  }
  for (const id of activeLenses) {
    const def = GAUGE_REGISTRY[id];
    if (def?.filter.axis === 'taskTypes') filterGroups.push({ taskTypes: def.filter.values });
  }

  const queryParams = {
    role,
    // When filterGroups has anything, the backend ignores the flat axes —
    // we still pass them so the no-filter-groups path stays correct.
    ...(filterGroups.length > 0
      ? { filterGroups }
      : {
          triggerTypes: activeTriggers.size > 0 ? Array.from(activeTriggers) : undefined,
          categories: activeCategories.size > 0 ? Array.from(activeCategories) : undefined,
          taskTypes: activeSubtypes.size > 0 ? Array.from(activeSubtypes) : undefined,
        }),
    q: [identifier.trim(), text.trim()].filter(Boolean).join(' ') || undefined,
    assignedToUserId: onlyMine ? myUserId : undefined,
    bomId: bomFilter?.bomId,
    page,
    limit: rowsPerPage,
  };

  const { data: worklist, isLoading, isError, error, refetch, isFetching } = useOrchestratorWorklist(queryParams);
  const { data: countsData } = useOrchestratorCounts(role, onlyMine ? myUserId : undefined, bomFilter?.bomId);
  const { data: kpisData } = useOrchestratorKpis(role);
  const executeAction = useExecuteAction();

  // ── Derived metrics ──────────────────────────────────────

  const triggerCounts = countsData?.counts ?? {};
  // Per-category counts power the user-pinned "+" gauges. Backend returns
  // `countsByCategory: { RFQ, SUPPLIER_LINK, … }` next to the trigger counts
  // off the same filtered universe — see countByCategory in the WI service.
  const categoryCounts = (countsData as any)?.countsByCategory ?? {};
  const taskTypeCounts = (countsData as any)?.countsByTaskType ?? {};
  const totalCount = countsData?.total ?? 0;
  // "Mine" total is unfilterable from the backend response shape today;
  // when assignedToUserId is the active filter, total reflects mine.
  const myCount = onlyMine ? totalCount : 0;

  // ── Action handlers ──────────────────────────────────────

  const onAssign = (workItemId: string) => {
    if (!myUserId) {
      // No auth context yet — show a hint
      alert('Auth context not available; assign disabled in this preview build.');
      return;
    }
    executeAction.mutate({
      workItemId,
      actionId: 'ASSIGN_WORK_ITEM',
      input: { assigneeUserId: myUserId, assigneeUserName: 'Me' },
    });
  };

  const onResolve = (workItemId: string) => {
    executeAction.mutate({
      workItemId,
      actionId: 'RESOLVE_WORK_ITEM',
      input: {},
    });
    setMenuAnchor(null);
  };

  const onDismiss = (workItemId: string) => {
    executeAction.mutate({
      workItemId,
      actionId: 'DISMISS_WORK_ITEM',
      input: { reason: 'Dismissed from worklist' },
    });
    setMenuAnchor(null);
  };

  const onSnooze = (workItemId: string) => {
    const snoozedUntil = new Date();
    snoozedUntil.setDate(snoozedUntil.getDate() + 7);
    executeAction.mutate({
      workItemId,
      actionId: 'SNOOZE_WORK_ITEM',
      input: { snoozedUntil: snoozedUntil.toISOString() },
    });
    setMenuAnchor(null);
  };

  // ── Reusable styles ──────────────────────────────────────

  const actionIconBtnSx = {
    width: 30,
    height: 30,
    borderRadius: 1.25,
    color: 'text.secondary',
    transition: 'all 0.12s',
    '&:hover': {
      bgcolor: alpha(theme.palette.primary.main, 0.08),
      color: 'primary.main',
    },
  } as const;

  // ── Render ───────────────────────────────────────────────

  const items = worklist?.items ?? [];

  return (
    <Box sx={{ width: '100%', pb: 4 }}>
      {/* Page title — sits in the line of space immediately below the
          breadcrumb (mt: -1 nudges it up so it visually clings to the
          breadcrumb divider rather than the gauges below). Flush-left,
          same x as the breadcrumb's home icon. */}
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          mt: -1,
          mb: 1,
          pl: 0,
          ml: 0,
          lineHeight: 1.2,
        }}
      >
        {cfg.title}
      </Typography>

      {/* Header — gauges centered on the page; the refresh icon is absolutely
          positioned at the right edge so it doesn't shift the gauge group
          off-center. The search bar below uses the same page-center axis,
          so the two stay vertically aligned. */}
      <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 96, mb: 0 }}>

        {/* Gauge strip — registry-driven. The set of gauges in view is
            `effectiveGaugeIds` (user customization, or role defaults if the
            user hasn't touched the picker). Each gauge def owns its filter
            axis (`triggers` or `categories`); clicking applies it and clears
            the other axis to prevent empty AND intersections. Multi-select
            still ORs within an axis via the backend `$in`. */}
        {(() => {
          // Build the visible gauge list from the registry. Unknown ids in a
          // saved selection are filtered out so a registry edit can't brick a
          // user's localStorage.
          const visibleGauges: GaugeDef[] = effectiveGaugeIds
            .map((id) => GAUGE_REGISTRY[id])
            .filter((g): g is GaugeDef => !!g);
          // Each rendered gauge carries the filter axis + values + the icon
          // component (rendered as a small badge in the gauge's open gap).
          const gauges: Array<{
            id: string;
            label: string;
            color: string;
            value: number;
            triggers?: TriggerType[];
            categories?: string[];
            Icon: React.ComponentType<{ sx?: any }>;
          }> = visibleGauges.map((g) => ({
            id: g.id,
            label: g.label,
            color: g.color,
            value: g.getCount({ triggerCounts, categoryCounts, taskTypeCounts }),
            triggers: g.filter.axis === 'triggers' ? g.filter.values : undefined,
            categories: g.filter.axis === 'categories' ? g.filter.values : undefined,
            Icon: g.Icon,
          }));
          return (
        <Stack direction="row" spacing={4} sx={{ justifyContent: 'center', alignItems: 'flex-start', minWidth: 0 }}>
          {gauges.map((k) => {
            // Multi-select click semantics. A gauge has either `triggers` or
            // `categories` (never both). Clicking ADDS the gauge's filter
            // values to the active set (toggle); clicking an active gauge
            // REMOVES them. The other axis is left alone so a user can stack
            // categorically: "RFQ + Vendor Approval + Opportunity" all on at
            // once, ORed on the backend via the filterGroups payload.
            const filterTriggers = k.triggers;
            const filterCategories = k.categories;
            const isTriggerActive =
              !!filterTriggers && filterTriggers.length > 0 &&
              filterTriggers.some((t) => activeTriggers.has(t));
            const isCategoryActive =
              !!filterCategories && filterCategories.length > 0 &&
              filterCategories.some((c) => activeCategories.has(c as any));
            const isLens = GAUGE_REGISTRY[k.id]?.filter.axis === 'taskTypes';
            const isActive = isTriggerActive || isCategoryActive || (isLens && activeLenses.has(k.id));
            // Group key identifies which row in the tray this gauge maps to.
            // Trigger gauges use the registry id (e.g. "opportunity"); category
            // gauges use "cat-<CAT>" matching the tray's row key.
            const groupKey =
              filterCategories && filterCategories.length > 0
                ? `cat-${filterCategories[0]}`
                : `trig-${k.id}`;
            const isFocused = focusedGroupKey === groupKey;
            const applyFilter = () => {
              if (isLens) {
                setActiveLenses((prev) => {
                  const next = new Set(prev);
                  if (next.has(k.id)) next.delete(k.id);
                  else next.add(k.id);
                  return next;
                });
                setPage(0);
                return;
              }
              if (isActive && isFocused) {
                // Toggle OFF — clear this gauge's filter values + drop focus.
                if (filterTriggers && filterTriggers.length > 0) {
                  setActiveTriggers((prev) => {
                    const next = new Set(prev);
                    for (const t of filterTriggers) next.delete(t);
                    return next;
                  });
                } else if (filterCategories && filterCategories.length > 0) {
                  setActiveCategories((prev) => {
                    const next = new Set(prev);
                    for (const c of filterCategories) next.delete(c as any);
                    return next;
                  });
                  setCategorySubtypes((subs) => {
                    const m = new Map(subs);
                    for (const c of filterCategories) m.delete(c as any);
                    return m;
                  });
                }
                setFocusedGroupKey(null);
              } else if (isActive && !isFocused) {
                // Active but the tray is showing someone else — just SWITCH
                // the focused row. Don't disable this gauge.
                setFocusedGroupKey(groupKey);
                setSubFilterExpanded(true);
              } else {
                // Inactive → enable + focus. Sub-filters default ON: we
                // populate the per-category subtype set with every available
                // taskType for that category, and (for trigger gauges) every
                // trigger in the gauge's set. The user can uncheck individual
                // sub-chips to narrow.
                if (filterTriggers && filterTriggers.length > 0) {
                  setActiveTriggers((prev) => {
                    const next = new Set(prev);
                    for (const t of filterTriggers) next.add(t);
                    return next;
                  });
                } else if (filterCategories && filterCategories.length > 0) {
                  setActiveCategories((prev) => {
                    const next = new Set(prev);
                    for (const c of filterCategories) next.add(c as any);
                    return next;
                  });
                  setCategorySubtypes((subs) => {
                    const m = new Map(subs);
                    for (const c of filterCategories) {
                      const opts = SUBTYPES_BY_CATEGORY[c as CategoryId] ?? [];
                      m.set(c as any, new Set(opts.map((o) => o.value)));
                    }
                    return m;
                  });
                }
                setFocusedGroupKey(groupKey);
                setSubFilterExpanded(true);
              }
              setFiltersOpen(true);
              setPage(0);
            };
            return (
              <Box key={k.label} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                {/* 270° arc gauge — open at the bottom, count + bucket icon
                    stacked vertically at the center. The icon is the same
                    one the filter strip uses for this bucket; clicking it
                    filters the worklist by that bucket (click again to
                    clear). Gauges with no clean bucket mapping skip the
                    icon entirely. */}
                <Box sx={{ position: 'relative', width: 84, height: 84 }}>
                  <svg width={84} height={84} style={{ display: 'block', transform: 'rotate(135deg)' }}>
                    <circle
                      cx={42}
                      cy={42}
                      r={38}
                      fill="none"
                      stroke={k.color}
                      strokeWidth={6}
                      strokeLinecap="round"
                      strokeDasharray={`${0.75 * 2 * Math.PI * 38} ${2 * Math.PI * 38}`}
                    />
                  </svg>
                  {/* Number — centered in the arc. */}
                  <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center' }}>
                    <Typography
                      sx={{
                        fontSize: '1.7rem',
                        fontWeight: 700,
                        color: k.color,
                        lineHeight: 1,
                        fontVariantNumeric: 'tabular-nums',
                        letterSpacing: '-0.03em',
                      }}
                    >
                      {k.value}
                    </Typography>
                  </Box>

                  {/* Identity icon — clickable filter shortcut in the open
                      gap at the bottom of the 270° arc. Every registry gauge
                      has an Icon, so we always render it. */}
                  <Tooltip
                    title={isActive ? `Clear ${k.label} filter` : `Filter by ${k.label}`}
                  >
                    <IconButton
                      size="small"
                      onClick={applyFilter}
                      sx={{
                        position: 'absolute',
                        bottom: -2,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: 22,
                        height: 22,
                        p: 0,
                        color: isActive ? '#fff' : k.color,
                        bgcolor: isActive ? k.color : 'transparent',
                        '&:hover': {
                          bgcolor: isActive ? k.color : alpha(k.color, 0.12),
                        },
                      }}
                    >
                      <k.Icon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Typography
                  sx={{ fontSize: '0.7rem', fontWeight: 600, color: 'text.secondary', textAlign: 'center', maxWidth: 96, lineHeight: 1.3, letterSpacing: '0.01em' }}
                >
                  {k.label}
                </Typography>
              </Box>
            );
          })}

          {/* "+" — opens the picker to customize the gauge strip. The picker
              now governs every gauge (including the role defaults), so a user
              can deselect a default they don't care about. */}
          <Box sx={{ display: 'flex', alignItems: 'center', alignSelf: 'flex-start', mt: '24px' }}>
            <GaugePicker
              role={role}
              selection={effectiveGaugeIds}
              onChange={(next) => setUserGaugeSelection(next)}
            />
          </Box>
        </Stack>
          );
        })()}

        <Tooltip title={isFetching ? 'Refreshing…' : 'Refresh'}>
          <span style={{ position: 'absolute', right: 0, top: 8 }}>
            <IconButton
              size="small"
              onClick={() => refetch()}
              disabled={isFetching}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* Active filter tray — one uniform row per active gauge group.
          Categories and trigger gauges share the same row anatomy:
            [GaugeChip + Icon + ✕]  [sub-chip · sub-chip · …]
          So Intake and RFQ render identically. Each row is one clause in
          the backend `$or` query. */}
      {(() => {
        // Build the unified group list — categories first, then any active
        // trigger gauges (one row per gauge whose triggers overlap with
        // activeTriggers). Each entry shares the same render shape so the
        // loop below is uniform.
        type ActiveGroup =
          | { kind: 'category'; key: string; label: string; ink: string; Icon: React.ComponentType<{ sx?: any }>;
              subOptions: Array<{ value: string; label: string }>;
              activeSubs: Set<string>;
              toggleSub: (v: string) => void;
              remove: () => void; }
          | { kind: 'trigger'; key: string; label: string; ink: string; Icon: React.ComponentType<{ sx?: any }>;
              subOptions: Array<{ value: string; label: string }>;
              activeSubs: Set<string>;
              toggleSub: (v: string) => void;
              remove: () => void; };
        const groups: ActiveGroup[] = [];

        // Category rows
        for (const cat of activeCategories) {
          const meta = WI_TYPE_META[cat];
          const subOptions = SUBTYPES_BY_CATEGORY[cat] ?? [];
          const activeSubs = categorySubtypes.get(cat) ?? new Set<string>();
          groups.push({
            kind: 'category',
            key: `cat-${cat}`,
            label: meta.label,
            ink: meta.ink,
            Icon: meta.Icon,
            subOptions,
            activeSubs,
            toggleSub: (taskType: string) => {
              setCategorySubtypes((prev) => {
                const next = new Map(prev);
                const cur = new Set(next.get(cat) ?? []);
                if (cur.has(taskType)) cur.delete(taskType); else cur.add(taskType);
                if (cur.size === 0) next.delete(cat); else next.set(cat, cur);
                return next;
              });
              setPage(0);
            },
            remove: () => {
              setActiveCategories((prev) => { const n = new Set(prev); n.delete(cat); return n; });
              setCategorySubtypes((prev) => { const n = new Map(prev); n.delete(cat); return n; });
              setPage(0);
            },
          });
        }

        // Trigger-gauge rows — one per registry gauge whose triggers overlap
        // with activeTriggers. Trigger label maps come from TRIGGER_CONFIG so
        // sub-chips read as the same human label the dashboard uses.
        for (const id of effectiveGaugeIds) {
          const def = GAUGE_REGISTRY[id];
          if (!def || def.filter.axis !== 'triggers') continue;
          const myTriggers = def.filter.values as TriggerType[];
          const overlap = myTriggers.filter((t) => activeTriggers.has(t));
          if (overlap.length === 0) continue;
          const cfgFor = (t: TriggerType) => (TRIGGER_CONFIG as any)[t];
          const subOptions = myTriggers.map((t) => ({
            value: t,
            label: cfgFor(t)?.label ?? t,
          }));
          groups.push({
            kind: 'trigger',
            key: `trig-${id}`,
            label: def.label,
            ink: def.color,
            Icon: def.Icon,
            subOptions,
            activeSubs: new Set<string>(overlap),
            toggleSub: (trig: string) => {
              setActiveTriggers((prev) => {
                const n = new Set(prev);
                if (n.has(trig as TriggerType)) n.delete(trig as TriggerType);
                else n.add(trig as TriggerType);
                return n;
              });
              setPage(0);
            },
            remove: () => {
              setActiveTriggers((prev) => {
                const n = new Set(prev);
                for (const t of myTriggers) n.delete(t);
                return n;
              });
              setPage(0);
            },
          });
        }

        if (groups.length === 0) return null;
        // Collapsed: render nothing; gauges still show active state above.
        if (!subFilterExpanded) return null;
        // Show only the focused group's row. Falls back to the most-recent
        // (last) group when no focus is set yet (first paint after enable).
        const focused = groups.find((g) => g.key === focusedGroupKey) ?? groups[groups.length - 1];
        const renderGroups = focused ? [focused] : [];
        if (renderGroups.length === 0) return null;

        return (
          <Box ref={subFilterRef} sx={{ mt: 1, mb: 2, display: 'flex', justifyContent: 'center' }}>
            <Box sx={{ maxWidth: 980, width: '100%' }}>
              <Stack spacing={0.75}>
                {renderGroups.map((g) => (
                  <Stack
                    key={g.key}
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    flexWrap="wrap"
                    rowGap={0.5}
                  >
                    {/* No leading identity chip — the gauge above is already
                        highlighted (you see which one is active), and the
                        sub-chips are tinted in the group's color. Removing
                        the group = click the active gauge again. Dropping
                        the chip avoids the "is this a sub-option too?"
                        confusion from having two pill styles side-by-side. */}
                    {g.subOptions.length === 0 && (
                      <Typography sx={{ color: g.ink, fontSize: '0.72rem', fontWeight: 600 }}>
                        {g.label} — all
                      </Typography>
                    )}
                    {g.subOptions.map((opt) => {
                      const on = g.activeSubs.has(opt.value);
                      // For trigger sub-options, we have a real count from
                      // triggerCounts. For category sub-options (taskType)
                      // we don't have a count today — pass 0 so the badge
                      // renders muted; later we can wire a per-taskType
                      // count endpoint if useful.
                      const count =
                        g.kind === 'trigger'
                          ? (triggerCounts[opt.value as TriggerType] ?? 0)
                          : 0;
                      return (
                        <SubChip
                          key={opt.value}
                          label={opt.label}
                          count={count}
                          active={on}
                          ink={g.ink}
                          soft={alpha(g.ink, 0.10)}
                          onClick={() => {
                            g.toggleSub(opt.value);
                            // Customizing keeps the tray expanded.
                            setSubFilterExpanded(true);
                          }}
                        />
                      );
                    })}
                  </Stack>
                ))}

                {groups.length > 1 && (
                  <Stack direction="row" justifyContent="flex-end">
                    <Button
                      size="small"
                      onClick={() => {
                        setActiveCategories(new Set());
                        setCategorySubtypes(new Map());
                        setActiveTriggers(new Set());
                        setPage(0);
                      }}
                      sx={{ fontSize: '0.7rem', textTransform: 'none' }}
                    >
                      Clear all
                    </Button>
                  </Stack>
                )}
              </Stack>
            </Box>
          </Box>
        );
      })()}

      {/* Search bar — centered under the gauges with ~2 lines of breathing
          room (mt 5) so the gauges stay visually anchored, and 2 lines of
          breathing room below before the table (mb 6 ≈ 48px) when the filter
          strip is closed. When the filter strip is open it owns the spacing
          to the table via its own mb. */}
      <Box sx={{ mt: 5, mb: filtersOpen || activeTriggers.size > 0 ? 1.5 : 6 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mx: 'auto',
            maxWidth: 760,
            width: '100%',
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <UnifiedSearchBar
              value={searchValue}
              onChange={handleSearchChange}
              fetchBoms={fetchBoms}
              onSearch={() => refetch()}
              placeholders={{ identifier: 'Part or MPN', text: 'Alt, description…', bom: 'All BOMs' }}
            />
          </Box>
          <Tooltip title={filtersOpen ? 'Hide filters' : 'Show filters'}>
            <IconButton
              size="small"
              onClick={() => setFiltersOpen((v) => !v)}
              sx={{
                flexShrink: 0,
                border: '1px solid',
                borderColor: filtersOpen || activeTriggers.size > 0
                  ? alpha('#0f172a', 0.35)
                  : alpha('#0f172a', 0.12),
                bgcolor: filtersOpen || activeTriggers.size > 0
                  ? alpha('#0f172a', 0.04)
                  : '#fff',
                borderRadius: 999,
                width: 40,
                height: 40,
              }}
            >
              <FilterIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* TYPE chip strip + Subtype dropdown deleted.
          The gauge strip above is the canonical category-filter surface now —
          clicking RFQ / Vendor Approval / etc. applies the same filter the
          chips used to. The Tier-2 sub-filter chips for trigger gauges
          (Opportunity → New alternative / Cost-down / Allocation) live
          directly under the gauge stack (rendered in the gauges block).
          Per-category Subtype filtering can return as a contextual row
          there too if buyers ask for it. */}

      {/* Error state */}
      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load worklist. {(error as any)?.message || 'Try refreshing.'}
        </Alert>
      )}

      {/* Worklist table. Paper keeps the rounded outer shell; the inner
          TableContainer owns horizontal scroll so a too-wide table (sidebar
          open + narrow laptop) scrolls inside the card instead of pushing
          the page out and triggering a page-level horizontal scrollbar.
          Background is a warm neutral grey (was slate-blue '#f8fafc' which
          bled into the expanded panels and read as "this whole row is
          selected" — confusing). Row separation still comes from each row's
          `boxShadow` + the borderSpacing gap. */}
      <Paper elevation={0} sx={{
        border: '1px solid', borderColor: theme.palette.divider, borderRadius: 2,
        overflow: 'hidden', bgcolor: '#fafafa',
      }}>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ borderCollapse: 'separate', borderSpacing: '0 10px', px: 1, minWidth: 980 }}>
            <TableHead>
              <TableRow sx={{
                '& th': {
                  fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase',
                  letterSpacing: '0.04em', color: theme.palette.text.secondary,
                  py: 1.25, border: 0, whiteSpace: 'nowrap',
                },
              }}>
                <TableCell sx={{ width: 36, pl: 1 }} />
                <TableCell sx={{ pl: 1 }}>Master Part</TableCell>
                <TableCell>Trigger</TableCell>
                <TableCell>Priority</TableCell>
                <TableCell>Due</TableCell>
                <TableCell align="right">$ at risk</TableCell>
                <TableCell align="right">Where used</TableCell>
                <TableCell>Resolution</TableCell>
                <TableCell align="center">Best&nbsp;FFF</TableCell>
                <TableCell align="right">Best $ candidate</TableCell>
                <TableCell align="right" sx={{ pr: 2 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading && Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={`s-${i}`} sx={{ '& td': { border: 0, py: 1.5, bgcolor: '#fff' } }}>
                  <TableCell sx={{ pl: 2 }} colSpan={11}>
                    <Skeleton variant="rectangular" height={48} />
                  </TableCell>
                </TableRow>
              ))}

              {!isLoading && items.length === 0 && (
                <TableRow sx={{ '& td': { border: 0, py: 6, bgcolor: '#fff', textAlign: 'center' } }}>
                  <TableCell colSpan={11}>
                    <Typography variant="body2" color="text.secondary">
                      Your queue is clear.
                    </Typography>
                    <Typography variant="caption" color="text.disabled">
                      New PCN, EOL, and alternative-discovery cases will appear here.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}

              {items.map((item: any) => {
                const sevColor = SEVERITY_COLOR[item.severity as Severity] ?? '#64748b';
                // BOM-driven priority (replaces severity as the row's signal once present)
                const prio = item.priority;
                const prioColor = prio?.tier ? (PRIORITY_COLOR[prio.tier] ?? '#94a3b8') : '#64748b';
                const prioDriver = prio?.primaryReasonCode
                  ? (PRIORITY_DRIVER_LABEL[prio.primaryReasonCode] ?? undefined)
                  : undefined;
                // The left accent + expand stripe follow priority when scored,
                // else fall back to legacy severity color.
                const stripeColor = prio?.tier ? prioColor : sevColor;
                const subj = item.subjectSnapshot || {};
                const dueLabel = formatDueLabel(item.dueDate);
                const slaLeft = formatSlaLeft(item.priority?.slaDueAt);
                const isDrawingCollab = isDrawingCollabTrigger(item.triggerType);
                const isExpanded = !isDrawingCollab && expanded.has(item.workItemId);
                const tp = item.taskPayload ?? {};
                // A step of a change's approval workflow: the change, not a part, is the subject.
                const isChange = item.taskType === 'CHANGE_APPROVAL';
                // A supplier's change request from a drawing area: a draft change to review.
                const isSupplierRequest = item.taskType === 'SUPPLIER_CHANGE_REQUEST';
                const firstUse = isSupplierRequest ? (tp.occurrences ?? [])[0] : undefined;
                // A bill's package misses required documents.
                const isPackage = item.taskType === 'PACKAGE_GAPS_REVIEW';
                const changeDue = isChange ? dueIn(tp.dueAt) : null;
                return (
                  <React.Fragment key={item.workItemId}>
                  <TableRow
                    onClick={() => (isDrawingCollab ? openDrawingThread(item) : toggleExpand(item.workItemId))}
                    sx={{
                      // Option 4 — hybrid card + severity stripe. Each row reads
                      // as a self-contained card: 1px slate-200 border via per-cell
                      // top/bottom + first/last vertical borders, 10px rounded
                      // corners, soft resting shadow, hover-lift shadow + border.
                      // Stripe is a 4px borderLeft on the first cell, colored by
                      // severity (replaces the old clipped absolute Box).
                      cursor: 'pointer',
                      position: 'relative',
                      boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
                      transition: 'box-shadow 0.15s ease',
                      '& > td': {
                        border: 0,
                        // Fixed height + lighter padding so every row is the
                        // same height (whether or not it has a description /
                        // extra chips) and the card is shorter than the old
                        // py:1.75 footprint. The meta line is single-line now
                        // (PartIdentity), so content height is consistent and
                        // the fixed height holds uniformly across eng + buyer.
                        height: 72,
                        py: 0.75,
                        bgcolor: '#fff',
                        borderTop: '1px solid #e2e8f0',
                        borderBottom: '1px solid #e2e8f0',
                      },
                      '& > td:first-of-type': {
                        borderLeft: `4px solid ${stripeColor}`,
                        borderTopLeftRadius: 10,
                        borderBottomLeftRadius: 10,
                      },
                      '& > td:last-of-type': {
                        borderRight: '1px solid #e2e8f0',
                        borderTopRightRadius: 10,
                        borderBottomRightRadius: 10,
                      },
                      '&:hover': {
                        boxShadow: '0 6px 16px rgba(15, 23, 42, 0.08)',
                      },
                      '&:hover > td': {
                        bgcolor: '#fafbfc',
                        borderTopColor: '#cbd5e1',
                        borderBottomColor: '#cbd5e1',
                      },
                      '&:hover > td:last-of-type': { borderRightColor: '#cbd5e1' },
                    }}
                  >
                    <TableCell sx={{ width: 36, pl: 1.25, pr: 0 }}>
                      {/* Drawing-collab rows deep-link to the canvas instead
                          of expanding. We used to render a small chat-bubble
                          here as a "this navigates" hint, but the 40×40
                          WiTypeIcon next to it already shows the type — the
                          small icon was redundant. Cell stays empty for
                          drawing-collab; the row's onClick still navigates. */}
                      {!isDrawingCollab && (
                        <IconButton size="small" sx={{ p: 0.25 }} aria-label="Toggle details">
                          {isExpanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                        </IconButton>
                      )}
                    </TableCell>
                    <TableCell sx={{ pl: 1, minWidth: 220, maxWidth: 360 }}>
                      {isChange ? (
                        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
                          <WiTypeIcon category={item.category} size={40} />
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', fontFamily: "'JetBrains Mono', monospace" }}>
                              {/* The item takes the change's severity: its priority. */}
                              <ChangeSeverityMark severity={item.severity} />{' '}
                              {subj.name}
                            </Typography>
                            <Typography noWrap sx={{ fontSize: '0.78rem', color: 'text.secondary', maxWidth: 300 }}>
                              {subj.subtitle}
                            </Typography>
                          </Box>
                        </Stack>
                      ) : (
                      <PartIdentity
                        partId={item.subjectId}
                        partNumber={item.masterPart?.partNumber ?? subj.partNumber ?? subj.name ?? item.subjectId}
                        mpn={item.masterPart?.mpn ?? subj.mpn}
                        manufacturer={item.masterPart?.manufacturer ?? subj.manufacturer}
                        description={item.masterPart?.description ?? subj.description}
                        category={item.masterPart?.category}
                        lifecycleStage={item.masterPart?.lifecycleStage}
                        thumbnailUrl={item.masterPart?.thumbnailUrl}
                        isCritical={item.masterPart?.isCritical ?? subj.isCritical}
                        // Surface the work-item type as the leading 40×40 icon
                        // instead of the part-category shape. The part identity
                        // (PN, MPN, manufacturer, description) is right next to
                        // it; the WI type is the more useful "what am I looking
                        // at" cue when scanning the inbox.
                        prefixIcon={<WiTypeIcon category={item.category} size={40} />}
                      />
                      )}
                    </TableCell>
                    <TableCell><TriggerBadge trigger={item.triggerType as TriggerType} /></TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {prio?.tier ? (
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Box
                            sx={{
                              width: 9, height: 9, borderRadius: '50%', flexShrink: 0,
                              bgcolor: prioColor,
                              boxShadow: `0 0 0 3px ${PRIORITY_HALO[prio.tier] ?? 'transparent'}`,
                            }}
                          />
                          <Typography sx={{ fontWeight: 700, color: '#1d2532', fontSize: '0.82rem', lineHeight: 1 }}>
                            {prio.tier}
                          </Typography>
                          {prioDriver && (
                            <Typography sx={{ color: '#94a3b8', fontSize: '0.8rem', lineHeight: 1 }}>
                              ·&nbsp;{prioDriver}
                            </Typography>
                          )}
                        </Stack>
                      ) : (
                        <Typography variant="caption" color="text.disabled">—</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {isChange && Number(tp.escalationLevel) > 0 ? (
                        <Tooltip title={`Escalated${Array.isArray(tp.escalatedTo) && tp.escalatedTo.length ? ` to ${tp.escalatedTo.join(', ')}` : ''}`} arrow>
                          <Typography variant="caption" sx={{ fontWeight: 700, color: 'error.main' }}>
                            Escalated{changeDue ? ` · ${changeDue.label}` : ''}
                          </Typography>
                        </Tooltip>
                      ) : changeDue ? (
                        <Typography variant="caption" sx={{ fontWeight: 600, color: changeDue.overdue ? 'error.main' : 'warning.dark' }}>
                          {changeDue.label}
                        </Typography>
                      ) : slaLeft ? (
                        <Tooltip title={slaLeft.full} arrow>
                          <Stack direction="row" alignItems="center" spacing={0.4} sx={{ color: slaLeft.color, width: 'fit-content' }}>
                            <ClockIcon sx={{ fontSize: 13 }} />
                            <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.72rem', color: 'inherit' }}>
                              {slaLeft.short}
                            </Typography>
                          </Stack>
                        </Tooltip>
                      ) : dueLabel
                          ? <Typography variant="caption" sx={{ fontWeight: 500 }}>{dueLabel}</Typography>
                          : <Typography variant="caption" color="text.disabled">—</Typography>}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                        {formatRevenue(item.exposure?.revenueAtRisk)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {isSupplierRequest && firstUse ? (
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                          {firstUse.bomName ?? 'Bill'} · L{firstUse.depth}
                          {(tp.occurrences?.length ?? 0) > 1 ? ` +${tp.occurrences.length - 1}` : ''}
                        </Typography>
                      ) : (
                      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                        {(item.exposure?.bomsAffected ?? 0)} BOM{(item.exposure?.bomsAffected ?? 0) !== 1 ? 's' : ''}
                        {' · '}
                        {(item.exposure?.linesAffected ?? 0)} line{(item.exposure?.linesAffected ?? 0) !== 1 ? 's' : ''}
                      </Typography>
                      )}
                    </TableCell>
                    {/* Resolution column. For alts-based triggers: counts of
                        alternatives + approvals. For drawing-collab: supplier
                        the thread belongs to + rev anchor + drawing-collab
                        thread state. The two render branches occupy the same
                        cell so the surrounding column layout stays stable. */}
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {isPackage ? (
                        <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                          <Typography variant="body2" noWrap sx={{ fontSize: '0.8rem', fontWeight: 600, maxWidth: 220 }}>
                            {tp.counts?.fail ?? 0} missing
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                            of {tp.counts?.items ?? 0} items
                          </Typography>
                        </Stack>
                      ) : isSupplierRequest ? (
                        <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                          <Typography variant="body2" noWrap sx={{ fontSize: '0.8rem', fontWeight: 600, maxWidth: 220 }}>
                            Change request · {tp.supplierDisplayName ?? 'supplier'}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                            {tp.changeNumber ? `Draft ${tp.changeNumber}` : tp.problemReportNumber ?? ''}
                          </Typography>
                        </Stack>
                      ) : isChange ? (
                        <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                          <Typography variant="body2" noWrap sx={{ fontSize: '0.8rem', fontWeight: 600, maxWidth: 220 }}>
                            {tp.stepName ?? tp.reason ?? 'Approval'}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                            {tp.stage ?? tp.workflowName ?? ''}
                          </Typography>
                        </Stack>
                      ) : isDrawingCollab ? (
                        <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: '0.8rem',
                              fontWeight: 600,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              maxWidth: 220,
                            }}
                            noWrap
                          >
                            {tp.supplierDisplayName || tp.supplierId || 'Supplier pending'}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                            {tp.drawingRev ? `rev ${tp.drawingRev}` : 'no rev'}
                            {tp.category ? ` · ${String(tp.category).toLowerCase().replace(/_/g, ' ')}` : ''}
                          </Typography>
                        </Stack>
                      ) : (() => {
                        const alts = item.resolution?.altsCount ?? 0;
                        const approved = item.resolution?.approvedCount ?? 0;
                        if (alts === 0) {
                          return <Typography variant="caption" color="text.disabled">no alts</Typography>;
                        }
                        return (
                          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                            {alts} alt{alts !== 1 ? 's' : ''}{' · '}
                            <Box component="span" sx={{ fontWeight: 700, color: approved > 0 ? '#15803d' : 'text.secondary' }}>
                              {approved} approved
                            </Box>
                          </Typography>
                        );
                      })()}
                    </TableCell>
                    {/* Best FFF score among alts still awaiting review. NA for
                        drawing-collab rows (no candidate parts in play). */}
                    <TableCell align="center" sx={{ width: 60 }}>
                      {isDrawingCollab || isChange || isSupplierRequest || isPackage
                        ? <Typography variant="caption" color="text.disabled">—</Typography>
                        : <FffScoreGauge score={item.bestActionableFffScore} />}
                    </TableCell>
                    {/* Best $ candidate: price + diff% + lead-time. NA for
                        drawing-collab rows. */}
                    <TableCell align="right">
                      {isDrawingCollab || isChange || isSupplierRequest || isPackage ? (
                        <Typography variant="caption" color="text.disabled">—</Typography>
                      ) : (() => {
                        const bc = item.bestCandidate;
                        if (!bc) return <Typography variant="caption" color="text.disabled">—</Typography>;
                        const diff = bc.diffPct;
                        const diffColor = diff == null ? 'text.secondary' : diff < 0 ? '#15803d' : diff > 0 ? '#b91c1c' : 'text.secondary';
                        return (
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.85rem', lineHeight: 1.2 }}>
                              {bc.bestPrice != null ? `$${bc.bestPrice.toFixed(2)}` : '—'}
                            </Typography>
                            <Typography variant="caption" sx={{ display: 'block', color: diffColor, fontWeight: 600, fontSize: '0.7rem' }}>
                              {diff != null ? `${diff >= 0 ? '+' : ''}${diff.toFixed(1)}%` : ''}
                              {diff != null && bc.leadTimeWeeks != null ? ' · ' : ''}
                              {bc.leadTimeWeeks != null ? `${bc.leadTimeWeeks}w` : ''}
                            </Typography>
                          </Box>
                        );
                      })()}
                    </TableCell>
                    <TableCell align="right" sx={{ pr: 1.5 }}>
                      <Stack
                        direction="row" spacing={0.25} justifyContent="flex-end"
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          display: 'inline-flex', p: 0.25, borderRadius: 1.5,
                          bgcolor: alpha(theme.palette.text.primary, 0.025),
                          border: '1px solid', borderColor: alpha(theme.palette.text.primary, 0.06),
                        }}
                      >
                        <Tooltip title={isDrawingCollab ? 'Open drawing' : isChange ? 'Open change' : 'Open'} arrow placement="top">
                          <IconButton
                            size="small"
                            sx={actionIconBtnSx}
                            onClick={() => isDrawingCollab
                              ? openDrawingThread(item)
                              : isChange
                                ? navigate(`/changes/${tp.changeId}`)
                                : navigate(`/orchestrator/work-items/${item.workItemId}`)}
                          >
                            {isDrawingCollab
                              ? <DrawingIcon sx={{ fontSize: 16 }} />
                              : <OpenIcon sx={{ fontSize: 16 }} />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={item.assigneeUserId ? 'Reassign' : 'Assign'} arrow placement="top">
                          <IconButton
                            size="small"
                            sx={{
                              ...actionIconBtnSx,
                              color: item.assigneeUserId ? '#2563eb' : actionIconBtnSx.color,
                            }}
                            onClick={() => onAssign(item.workItemId)}
                          >
                            <AssignIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                        <Box sx={{ width: '1px', alignSelf: 'stretch', bgcolor: alpha(theme.palette.text.primary, 0.08), mx: 0.25 }} />
                        <Tooltip title="More actions" arrow placement="top">
                          <IconButton size="small" sx={actionIconBtnSx}
                            onClick={(e) => setMenuAnchor({ el: e.currentTarget, itemId: item.workItemId, step: isChange })}>
                            <MoreIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                  {isExpanded && (
                    // Drill-down container — Option A treatment: tinted
                    // panel (slate-100) wraps the whole expansion, with a
                    // left accent stripe in the row's severity color so the
                    // expansion visually anchors back to its parent row.
                    // The inner AlternativesTable keeps its own white Paper bg
                    // so it reads as a card floating on the tinted surface.
                    // `& > td` scopes the no-border treatment to the direct
                    // child only — nested table <td>s keep their own borders.
                    <TableRow sx={{ '& > td': { border: 0, py: 0, pl: 0, pr: 0 } }}>
                      <TableCell colSpan={11}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box
                            sx={{
                              position: 'relative',
                              bgcolor: '#f1f5f9',                  // slate-100
                              borderRadius: 1,
                              border: '1px solid #e2e8f0',         // slate-200
                              borderLeft: `4px solid ${stripeColor}`, // severity stripe
                              ml: 1.5,
                              mr: 1.5,
                              mb: 1,
                              pt: 1.5,
                              pb: 2,
                              pl: 2,
                              pr: 2,
                            }}
                          >
                            {item.category === 'RFQ' ? (
                              // Category-aware branch — RFQ work items render a
                              // labels-style inner card per RFQ (state-driven
                              // header pill + Case 1/2 body + state-gated
                              // actions) instead of the alternatives table.
                              <RfqWorkItemCard item={item} />
                            ) : isChange ? (
                              <ChangeApprovalCard item={item} />
                            ) : isSupplierRequest ? (
                              <SupplierChangeRequestCard item={item} />
                            ) : isPackage ? (
                              <PackageGapsCard item={item} />
                            ) : (
                              <WorklistRowDetail
                                workItemId={item.workItemId}
                                masterPartId={item.subjectId}
                                role={role}
                                busyActionId={altActionBusy}
                                currentBestPrice={item.currentBestPrice}
                                onOpenCompare={(altPartId, partCrossReferenceId) =>
                                  navigate(
                                    `/cross-references/compare/${item.subjectId}/${altPartId}` +
                                    `?workItemId=${encodeURIComponent(item.workItemId)}` +
                                    `&partCrossReferenceId=${encodeURIComponent(partCrossReferenceId)}` +
                                    `&role=${encodeURIComponent(role)}`,
                                  )
                                }
                                onAltAction={(actionId, altRow) => {
                                  // Stage the confirm dialog; the actual mutate
                                  // fires from the dialog's onConfirm with the
                                  // optional rationale captured by the textarea.
                                  const kind: AltActionKind = actionId === 'APPROVE_ALT_CONDITIONAL'
                                    ? 'conditional'
                                    : actionId.startsWith('APPROVE_') ? 'approve' : 'reject';
                                  setPendingAltAction({
                                    workItemId: item.workItemId,
                                    actionId,
                                    altRow,
                                    kind,
                                  });
                                }}
                              />
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={worklist?.total ?? 0}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10, 20, 50]}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          sx={{
            borderTop: '1px solid', borderColor: theme.palette.divider, bgcolor: '#fff',
            '& .MuiToolbar-root': { minHeight: 44 },
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: '0.75rem' },
          }}
        />
      </Paper>

      {/* More-actions menu */}
      <Menu
        anchorEl={menuAnchor?.el}
        open={!!menuAnchor}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              mt: 0.5, minWidth: 220, borderRadius: 1.5,
              border: '1px solid', borderColor: theme.palette.divider,
              boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)',
              '& .MuiMenuItem-root': { fontSize: '0.825rem', py: 0.875, px: 1.5, gap: 1 },
              '& .MuiListItemIcon-root': { minWidth: 28, color: 'text.secondary' },
            },
          },
        }}
      >
        <MenuItem onClick={() => menuAnchor && onSnooze(menuAnchor.itemId)}>
          <ListItemIcon><SnoozeIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Snooze 7 days</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => menuAnchor && onAssign(menuAnchor.itemId)}>
          <ListItemIcon><AssignIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Assign to me</ListItemText>
        </MenuItem>
        {!menuAnchor?.step && [
          <Divider key="d" sx={{ my: 0.5 }} />,
          <MenuItem key="r" onClick={() => menuAnchor && onResolve(menuAnchor.itemId)}>
            <ListItemIcon><CheckIcon fontSize="small" sx={{ color: '#15803d' }} /></ListItemIcon>
            <ListItemText>Mark resolved</ListItemText>
          </MenuItem>,
          <MenuItem key="x" onClick={() => menuAnchor && onDismiss(menuAnchor.itemId)}>
            <ListItemIcon><DismissIcon fontSize="small" sx={{ color: '#dc2626' }} /></ListItemIcon>
            <ListItemText>Dismiss</ListItemText>
          </MenuItem>,
        ]}
      </Menu>

      {executeAction.isPending && (
        <Box sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: 1500 }}>
          <Paper elevation={3} sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={14} />
            <Typography variant="caption">Applying action…</Typography>
          </Paper>
        </Box>
      )}

      {/* Approve/Reject confirmation dialog — staged by the alt-row buttons.
          The mutate fires from onConfirm with the (optional) rationale. */}
      <AltActionConfirmDialog
        kind={pendingAltAction?.kind ?? null}
        loading={executeAction.isPending}
        context={pendingAltAction ? {
          subjectLabel: pendingAltAction.altRow?.altPartNumber
            ? `${pendingAltAction.altRow.altPartNumber}`
            : pendingAltAction.altRow?.partCrossReferenceId,
          secondaryLabel: pendingAltAction.altRow?.altManufacturerName,
          role,
        } : undefined}
        onCancel={() => setPendingAltAction(null)}
        onConfirm={(rationale) => {
          if (!pendingAltAction) return;
          const { workItemId, actionId, altRow } = pendingAltAction;
          setAltActionBusy(altRow.partCrossReferenceId);
          executeAction.mutate(
            {
              workItemId,
              actionId,
              input: {
                partCrossReferenceId: altRow.partCrossReferenceId,
                rationale,
              },
            },
            {
              onSettled: () => {
                setAltActionBusy(null);
                setPendingAltAction(null);
              },
            },
          );
        }}
      />
    </Box>
  );
};

// ── Helpers ─────────────────────────────────────────────────

function formatDueLabel(due?: string | Date | null): string | null {
  if (!due) return null;
  const d = new Date(due);
  const days = Math.round((d.getTime() - Date.now()) / 86_400_000);
  if (days < 0) return `Overdue ${-days}d`;
  if (days < 14) return `${days}d`;
  if (days < 90) return `${Math.round(days / 7)}w`;
  return `${Math.round(days / 30)}mo`;
}

// Time left against the SLA response deadline (priority.slaDueAt = createdAt +
// tier SLA). Red when overdue, amber when < 8h, muted otherwise.
// Compact: `short` is just the magnitude (e.g. "19d"); the icon colour conveys
// state and `full` is the hover tooltip.
function formatSlaLeft(slaDueAt?: string | Date | null): { short: string; color: string; full: string } | null {
  if (!slaDueAt) return null;
  const diffH = (new Date(slaDueAt).getTime() - Date.now()) / 3_600_000;
  const overdue = diffH < 0;
  const a = Math.abs(diffH);
  const short = a < 1 ? `${Math.round(a * 60)}m` : a < 48 ? `${Math.round(a)}h` : `${Math.round(a / 24)}d`;
  return {
    short,
    color: overdue ? '#d2453f' : diffH < 8 ? '#c98a2e' : '#9aa3af',
    full: overdue ? `Overdue by ${short}` : `${short} left to respond`,
  };
}

export default OrchestratorReviewQueuePage;
